import React, { useEffect, useRef, useState } from 'react';
import '../components/css/CryptoPrices.css';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import ValidatorProfile from './ValidatorProfile';
import { fromBech32, toBech32 } from '@cosmjs/encoding';

const ACTIVITY_REFRESH_MS = 6000;
const stakePeriods = [
  { key: '24h', label: '24 hours' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '1 month' }
];

const changeDirection = (value) => Number(value) > 0 ? 'positive' : Number(value) < 0 ? 'negative' : 'neutral';
const hasValue = (value) => value !== null && value !== undefined && value !== '' && !['N/A', '—', '--'].includes(String(value));
const hasNumber = (value) => hasValue(value) && Number.isFinite(Number(value));
const formatStakeChange = (micro, exponent = 6, symbol = 'LUNC') => {
  if (micro === null || micro === undefined) return '—';
  const amount = Number(micro) / (10 ** exponent);
  if (!Number.isFinite(amount)) return '—';
  if (amount === 0) return `0 ${symbol}`;
  const magnitude = Math.abs(amount) < .01 ? '<0.01' : Math.abs(amount).toLocaleString('en-US', { maximumFractionDigits: 2 });
  return `${amount > 0 ? '+' : '−'}${magnitude} ${symbol}`;
};
const formatPowerChange = (points) => {
  if (points === null || points === undefined || !Number.isFinite(Number(points))) return '—';
  const amount = Number(points);
  if (amount === 0) return '0 pp';
  return `${amount > 0 ? '+' : '−'}${Math.abs(amount) < .00005 ? '<0.0001' : Math.abs(amount).toFixed(4)} pp`;
};
const formatRankChange = (ranks) => {
  const amount = Number(ranks);
  return amount > 0 ? `+${amount}` : `−${Math.abs(amount)}`;
};

const healthStatus = (validator) => {
  if (validator.jailed) return 'jailed';
  if (validator.status !== 'Active' && validator.status !== 'BOND_STATUS_BONDED') return 'trouble';
  const signingPercent = Number(validator.blocks?.percent);
  return validator.blocks?.percent != null && Number.isFinite(signingPercent) && signingPercent < 95
    ? 'trouble' : 'live';
};

const formatBlockTime = (value) => {
  if (!value) return 'Time unavailable';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Time unavailable' : date.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short'
  });
};

const renderActivityPanel = (title, panel, okLabel, showLastMiss = false) => {
  const summary = panel?.summary;
  const safeItems = Array.isArray(panel?.items) ? panel.items : [];
  const percent = summary?.percent ?? (safeItems.length
    ? (safeItems.filter((item) => item.status === okLabel).length / safeItems.length) * 100
    : null);

  return (
    <div className="activity-panel">
      <div className="activity-panel-head">
        <div>
          <h3>{title}</h3>
          <p>Newest first</p>
        </div>
        <strong>{percent != null ? `${Number(percent).toFixed(0)}%` : '—'}</strong>
      </div>
      {panel?.available === false ? <p className="activity-unavailable">{panel.reason}</p> :
        !panel ? <p className="activity-unavailable">Loading live activity...</p> :
          <>
            <div className="activity-strip" aria-label={title} aria-live="polite">
              {safeItems.map((item, index) => (
                <span
                  key={`${item.height || item.status || 'activity'}-${index}`}
                  className={`activity-dot ${item.severity || item.status}`}
                  title={`${item.height ? `Block ${item.height}: ` : ''}${item.status}${item.status === 'missed' && item.height === panel.lastMissed?.height ? ` · ${formatBlockTime(panel.lastMissed.time)}` : ''}`}
                />
              ))}
            </div>
            {showLastMiss && <p className="activity-last-missed">
              <span>Most recent missed block</span>
              <strong>{panel.lastMissed ? `#${Number(panel.lastMissed.height).toLocaleString()} · ${formatBlockTime(panel.lastMissed.time)}` : `None in the latest ${safeItems.length} blocks`}</strong>
            </p>}
          </>}
    </div>
  );
};

const ValidatorActivityPanels = ({ lcd, operator, chainKey }) => {
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    if (!operator) return undefined;

    let active = true;
    let controller = null;

    const loadActivity = async () => {
      if (controller) controller.abort();
      controller = new AbortController();

      try {
        const url = chainKey
          ? `${lcd}/api/cosmos/chains/${encodeURIComponent(chainKey)}/validators/${encodeURIComponent(operator)}/activity`
          : `${lcd}/api/validator/${encodeURIComponent(operator)}/activity`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`Activity API ${res.status}`);
        const data = await res.json();
        if (active) setActivity(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          if (active) setActivity({
            blocks: { available: false, reason: 'Recent block signing data is temporarily unavailable.' },
            oracle: { available: false, reason: 'Oracle voting data is temporarily unavailable.' },
          });
        }
      }
    };

    setActivity(null);
    loadActivity();
    const interval = setInterval(loadActivity, chainKey ? 60000 : ACTIVITY_REFRESH_MS);

    return () => {
      active = false;
      clearInterval(interval);
      if (controller) controller.abort();
    };
  }, [lcd, operator, chainKey]);

  return (
    <div className="activity-panels">
      {activity?.blocks?.available !== false && renderActivityPanel('Uptime by 100 latest blocks', activity?.blocks, 'signed', true)}
      {!chainKey && activity?.oracle?.available !== false && renderActivityPanel('Uptime by 100 latest oracle votes', activity?.oracle, 'proposed')}
    </div>
  );
};

const CryptoPrices = () => {
const LCD = '';

  const validatorsPerPage = 110;
  


  const [validators, setValidators] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [networkParams, setNetworkParams] = useSearchParams();
  const requestedStatus = ['all', 'active', 'live', 'inactive', 'trouble', 'jailed'].includes(networkParams.get('status')) ? networkParams.get('status') : 'live';
  const [statusFilter, setStatusFilter] = useState(requestedStatus);
  const [stakePeriod, setStakePeriod] = useState('24h');
  const [cosmosChains, setCosmosChains] = useState([]);
  const requestedNetwork = networkParams.get('chain') || 'lunc';
  const [selectedNetwork, setSelectedNetwork] = useState(requestedNetwork === 'terra' ? 'lunc' : requestedNetwork);
  useEffect(() => {
    setSelectedNetwork(requestedNetwork === 'terra' ? 'lunc' : requestedNetwork);
    setStatusFilter(requestedStatus);
    setSelectedValidator(null);
  }, [requestedNetwork, requestedStatus]);
  const [validatorsLoading, setValidatorsLoading] = useState(true);
  const [validatorsError, setValidatorsError] = useState('');
  const [selectedValidator, setSelectedValidator] = useState(null);
  const [validatorDetails, setValidatorDetails] = useState(null);
  const [insightMetrics, setInsightMetrics] = useState({});
  const [profileInsights, setProfileInsights] = useState(null);
  const requestedValidator = networkParams.get('validator');
  const handledValidatorLink = useRef('');
  useEffect(() => {
    const key = `${selectedNetwork}:${requestedValidator || ''}`;
    if (key === handledValidatorLink.current) return;
    if (!requestedValidator) { handledValidatorLink.current = key; return; }
    if (validators.some((v) => getOperator(v) === requestedValidator)) {
      handledValidatorLink.current = key;
      setSelectedValidator(requestedValidator);
    }
  }, [requestedValidator, selectedNetwork, validators]);
  const [detailsError, setDetailsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [favoriteValidators, setFavoriteValidators] = useState([]);

  // In-memory cache (fastest, but lost on refresh)
  const detailsCacheRef = useRef(new Map()); // operator -> details
  const inflightRef = useRef(new Map());     // operator -> AbortController
  const warmStartedRef = useRef(false);
  const filterKeyRef = useRef('');
const [logoErrors, setLogoErrors] = useState({});
const [cosmosLogos, setCosmosLogos] = useState({});
const [modalLogoError, setModalLogoError] = useState(false);
  const isCosmosNetwork = selectedNetwork !== 'lunc';
  const selectedChain = cosmosChains.find((chain) => chain.key === selectedNetwork);
  const selectedSymbol = isCosmosNetwork ? selectedChain?.symbol || '' : 'LUNC';
  const denomExponent = isCosmosNetwork ? Number(selectedChain?.denomExponent || 0) : 6;


  // ---------------- Helpers ----------------
  const getOperator = (v) =>
    v?.operator_address ||
    v?.operatorAddress ||
    v?.operator ||
    v?.address ||
    v?.valoper ||
    '';

  const formatLunc = (micro) => {
    const n = parseFloat(micro || 0);
    return (n / 1e6).toLocaleString();
  };
  const formatStake = (micro) => {
    if (micro === null || micro === undefined || micro === '') return '—';
    const amount = Number(micro || 0) / (10 ** denomExponent);
    return Number.isFinite(amount) ? amount.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—';
  };
  const formatCommission = (value) => {
    const rate = Number(value);
    return Number.isFinite(rate) ? `${(isCosmosNetwork ? rate * 100 : rate).toFixed(2)}%` : '—';
  };

  const formatCount = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString() : '—';
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    return Number.isFinite(n) ? `${n.toFixed(2)}%` : '—';
  };

  const formatMetric = (summary, doneKey = 'signed') => {
    if (!summary || !Number(summary.total)) return '—';
    return `${formatPercent(summary.percent)} (${formatCount(summary[doneKey])} / ${formatCount(summary.total)})`;
  };

  const renderWebsiteLink = (rawWebsite, stopPropagation = false) => {
    if (!rawWebsite) return '—';
    const safe = rawWebsite.startsWith('http') ? rawWebsite : `https://${rawWebsite}`;
    try {
      const host = new URL(safe).hostname;
      return (
        <a
          href={safe}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => stopPropagation && e.stopPropagation()}
        >
          {host}
        </a>
      );
    } catch {
      return rawWebsite;
    }
  };

  const toggleFavorite = (operatorAddr) => {
    if (!operatorAddr) return;
    setFavoriteValidators((prev) => {
      const updated = prev.includes(operatorAddr)
        ? prev.filter((id) => id !== operatorAddr)
        : [...prev, operatorAddr];

      localStorage.setItem('favoriteValidators', JSON.stringify(updated));
      return updated;
    });
  };

  // ---------------- sessionStorage cache helpers ----------------
  const ssKey = (op) => `validator_details_v3_${op}`;

  const loadFromSession = (op) => {
    try {
      const raw = sessionStorage.getItem(ssKey(op));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const saveToSession = (op, data) => {
    try {
      // keep it light; sessionStorage has limits
      sessionStorage.setItem(ssKey(op), JSON.stringify(data));
    } catch {
      // ignore quota errors
    }
  };

  // ---------------- Prefetch Details ----------------
  const prefetchDetails = async (operator) => {
    if (!operator || isCosmosNetwork) return;

    // already in memory cache
    if (detailsCacheRef.current.has(operator)) return;

    // check session cache
    const ss = loadFromSession(operator);
    if (ss) {
      detailsCacheRef.current.set(operator, ss);
      return;
    }

    // avoid duplicate inflight requests
    if (inflightRef.current.has(operator)) return;

    const controller = new AbortController();
    inflightRef.current.set(operator, controller);

    try {
      const url = `${LCD}/api/validator/${encodeURIComponent(operator)}/details`;
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) return;

      const data = await res.json();
      detailsCacheRef.current.set(operator, data);
      saveToSession(operator, data);
    } catch {
      // ignore prefetch errors
    } finally {
      inflightRef.current.delete(operator);
    }
  };

  // ---------------- Load favorites ----------------
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('favoriteValidators') || '[]');
      if (Array.isArray(stored)) setFavoriteValidators(stored);
    } catch {
      setFavoriteValidators([]);
    }
  }, []);

  useEffect(() => {
    let active = true;
    axios.get(`${LCD}/api/cosmos/chains`).then((response) => {
      if (active) setCosmosChains((response.data?.chains || []).filter((chain) => chain.key !== 'terra' && chain.validatorsAvailable));
    }).catch(() => {
      if (active) setValidatorsError('The Cosmos chain list is temporarily unavailable.');
    });
    return () => { active = false; };
  }, []);

  // ---------------- Fetch Batch Validators (compute voting power 4dp) ----------------
  useEffect(() => {
    let active = true;
    let requestId = 0;
    const fetchValidators = async (refresh = false) => {
      const currentRequest = ++requestId;
      if (!refresh) {
        setValidatorsLoading(true);
        setValidatorsError('');
        setSelectedValidator(null);
        setValidators([]);
        setFiltered([]);
        setSearch('');
        setCurrentPage(1);
        detailsCacheRef.current.clear();
        warmStartedRef.current = false;
      }
      try {
        const res = await axios.get(isCosmosNetwork
          ? `${LCD}/api/cosmos/chains/${encodeURIComponent(selectedNetwork)}/validators`
          : `${LCD}/api/validators/batch?includeInactive=1`);
        if (!active || currentRequest !== requestId) return;
        const all = isCosmosNetwork ? res.data?.validators || [] : res.data || [];

        const sorted = [...all].sort(
          (a, b) => {
            const aBonded = !a.jailed && (a.status === 'Active' || a.status === 'BOND_STATUS_BONDED');
            const bBonded = !b.jailed && (b.status === 'Active' || b.status === 'BOND_STATUS_BONDED');
            return Number(bBonded) - Number(aBonded) || parseFloat(b.tokens || 0) - parseFloat(a.tokens || 0);
          }
        );

        const totalActiveTokens = sorted.filter((v) => !v.jailed && (v.status === 'Active' || v.status === 'BOND_STATUS_BONDED')).reduce(
          (sum, v) => sum + parseFloat(v.tokens || 0),
          0
        );

        const ranked = sorted.map((v, i) => {
          const tokens = parseFloat(v.tokens || 0);
          const bonded = !v.jailed && (v.status === 'Active' || v.status === 'BOND_STATUS_BONDED');
          const vp = totalActiveTokens > 0 && bonded ? (tokens / totalActiveTokens) * 100 : 0;
          let owner = null;
          try { const operator = getOperator(v); owner = toBech32(operator.split('valoper1')[0], fromBech32(operator).data); } catch {}

          return {
            ...v,
            owner,
            displayIndex: i + 1,
            voting_power: vp.toFixed(4), // ✅ 4 decimals
          };
        });

        setValidators((previous) => {
          if (!refresh || !isCosmosNetwork) return ranked;
          const previousChanges = new Map(previous.map((validator) => [getOperator(validator), validator.stake_changes]));
          return ranked.map((validator) => ({ ...validator, stake_changes: previousChanges.get(getOperator(validator)) }));
        });
        if (!refresh) setFiltered(ranked);
        if (isCosmosNetwork) {
          axios.get(`${LCD}/api/cosmos/chains/${encodeURIComponent(selectedNetwork)}/validators/history`)
            .then(({ data }) => {
              if (!active || currentRequest !== requestId) return;
              const changes = data?.changesByOperator || {};
              setValidators((previous) => previous.map((validator) => ({ ...validator,
                stake_changes: changes[getOperator(validator)] || null })));
            }).catch(() => {});
        }
      } catch (err) {
        if (!active) return;
        console.error('❌ Failed to load validator batch:', err.message);
        if (!refresh) {
          setValidatorsError(err.response?.data?.message || (isCosmosNetwork ? 'Could not load this chain’s validators. Please choose another chain or try again.' : 'Could not load Terra Classic validators. Please try again.'));
          setValidators([]);
          setFiltered([]);
        }
      } finally {
        if (active && !refresh) setValidatorsLoading(false);
      }
    };

    fetchValidators();
    const refreshTimer = setInterval(() => fetchValidators(true), 120000);
    return () => { active = false; if (refreshTimer) clearInterval(refreshTimer); };
  }, [selectedNetwork, isCosmosNetwork]);

  // ---------------- Warm cache after list loads (fixes “slow after refresh”) ----------------
  useEffect(() => {
    if (isCosmosNetwork) return;
    if (!validators.length) return;
    if (warmStartedRef.current) return;

    warmStartedRef.current = true;

    // Warm top 20 + any favorites first
    const favoritesFirst = validators
      .filter((v) => favoriteValidators.includes(getOperator(v)))
      .map((v) => getOperator(v))
      .filter(Boolean);

    const top = validators
      .slice(0, 20)
      .map((v) => getOperator(v))
      .filter(Boolean);

    const toWarm = Array.from(new Set([...favoritesFirst, ...top])).slice(0, 25);

    toWarm.forEach((op, idx) => {
      // small stagger to avoid hammering backend at once
      setTimeout(() => prefetchDetails(op), idx * 150);
    });
  }, [validators, favoriteValidators, isCosmosNetwork]);

  // ---------------- Filter Search ----------------
  useEffect(() => {
    const lower = search.toLowerCase();
    const results = validators.filter((v) =>
      `${v.moniker || ''} ${getOperator(v)} ${v.owner || ''} ${insightMetrics[getOperator(v)]?.owner || ''} ${insightMetrics[getOperator(v)]?.feeder || ''}`.toLowerCase().includes(lower)
      && (statusFilter === 'all' || (statusFilter === 'active' ? !v.jailed && ['Active', 'BOND_STATUS_BONDED'].includes(v.status) : statusFilter === 'inactive' ? !v.jailed && !['Active', 'BOND_STATUS_BONDED'].includes(v.status) : healthStatus(v) === statusFilter))
    );
    setFiltered(results);
    const filterKey = `${selectedNetwork}:${statusFilter}:${search}`;
    if (filterKeyRef.current !== filterKey) setCurrentPage(1);
    filterKeyRef.current = filterKey;
  }, [search, validators, statusFilter, selectedNetwork, insightMetrics]);

  useEffect(() => { setInsightMetrics({}); }, [selectedNetwork]);
  useEffect(() => {
    setProfileInsights(null);
    if (!selectedValidator) return undefined;
    const controller = new AbortController();
    axios.get(`${LCD}/api/cosmos/chains/${selectedNetwork === 'lunc' ? 'terra' : encodeURIComponent(selectedNetwork)}/validators/${encodeURIComponent(selectedValidator)}/insights`, { signal: controller.signal })
      .then(({ data }) => { setProfileInsights(data); setInsightMetrics((old) => ({ ...old, [selectedValidator]: data })); })
      .catch(() => {});
    return () => controller.abort();
  }, [selectedValidator, selectedNetwork]);

  // ---------------- Fetch Validator Details (cache-first, timeout fallback) ----------------
  useEffect(() => {
    if (!selectedValidator) return;

    document.body.classList.add('modal-open');
    setValidatorDetails(null);
    setDetailsError(null);
    setModalLogoError(false);

    if (isCosmosNetwork) {
      const controller = new AbortController();
      const listData = validators.find((v) => getOperator(v) === selectedValidator) || {};
      const url = `${LCD}/api/cosmos/chains/${encodeURIComponent(selectedNetwork)}/validators/${encodeURIComponent(selectedValidator)}/details`;
      axios.get(url, { signal: controller.signal }).then((response) => {
        setValidatorDetails({ ...listData, ...response.data });
      }).catch((error) => {
        if (error.code === 'ERR_CANCELED') return;
        setDetailsError(error.response?.data?.message || 'Could not load live validator details.');
        setValidatorDetails(listData);
      });
      return () => { controller.abort(); document.body.classList.remove('modal-open'); };
    }

    // 1) memory cache
    const mem = detailsCacheRef.current.get(selectedValidator);
    if (mem) {
      const moniker =
        validators.find((v) => getOperator(v) === selectedValidator)?.moniker ||
        mem?.moniker ||
        'Unknown';
      setValidatorDetails({ ...mem, operator_address: selectedValidator, moniker });
      return () => document.body.classList.remove('modal-open');
    }

    // 2) session cache
    const ss = loadFromSession(selectedValidator);
    if (ss) {
      detailsCacheRef.current.set(selectedValidator, ss);
      const moniker =
        validators.find((v) => getOperator(v) === selectedValidator)?.moniker ||
        ss?.moniker ||
        'Unknown';
      setValidatorDetails({ ...ss, operator_address: selectedValidator, moniker });
      return () => document.body.classList.remove('modal-open');
    }

    // 3) network fetch with timeout
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);

    const fetchDetails = async () => {
      try {
        const url = `${LCD}/api/validator/${encodeURIComponent(selectedValidator)}/details`;
        const res = await fetch(url, { signal: controller.signal });

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`Details API ${res.status}: ${text || res.statusText}`);
        }

        const details = await res.json();
        detailsCacheRef.current.set(selectedValidator, details);
        saveToSession(selectedValidator, details);

        const moniker =
          validators.find((v) => getOperator(v) === selectedValidator)?.moniker ||
          details?.moniker ||
          'Unknown';

        setValidatorDetails({ ...details, operator_address: selectedValidator, moniker });
      } catch (err) {
        console.error('❌ Failed to fetch validator details:', err);
        setDetailsError(
          err.name === 'AbortError'
            ? 'Request timed out (20s). New validators can be slow the first time.'
            : (err.message || 'Failed to load details.')
        );
        setValidatorDetails({ moniker: 'Unknown', operator_address: selectedValidator });
      } finally {
        clearTimeout(timeout);
      }
    };

    fetchDetails();

    return () => {
      clearTimeout(timeout);
      controller.abort();
      document.body.classList.remove('modal-open');
    };
  }, [selectedValidator, validators, isCosmosNetwork, selectedNetwork]);

  // ---------------- Pagination ----------------
  const pageSize = isCosmosNetwork ? 40 : validatorsPerPage;
  const indexOfLast = currentPage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;

  const favoriteList = filtered.filter((v) => favoriteValidators.includes(getOperator(v)));
  const otherList = filtered.filter((v) => !favoriteValidators.includes(getOperator(v)));
  const sortedList = [...favoriteList, ...otherList];

  const currentValidators = sortedList.slice(indexOfFirst, indexOfLast);
  const visibleOperators = currentValidators.map(getOperator).filter(Boolean).join(',');
  const delegatorComparisonHeight = validators.find((v) => v.stake_changes?.[stakePeriod]?.comparisonHeight)?.stake_changes?.[stakePeriod]?.comparisonHeight || null;
  useEffect(() => {
    if (!visibleOperators) return undefined;
    const controller = new AbortController();
    const operators = visibleOperators.split(',');
    const load = async () => {
      for (let index = 0; index < operators.length; index += 12) {
        if (controller.signal.aborted) break;
        try {
          const { data } = await axios.get(`${LCD}/api/cosmos/chains/${selectedNetwork === 'lunc' ? 'terra' : encodeURIComponent(selectedNetwork)}/validators/metrics`, {
            params: { operators: operators.slice(index, index + 12).join(','), ...(delegatorComparisonHeight ? { height: delegatorComparisonHeight } : {}) }, signal: controller.signal,
          });
          if (!controller.signal.aborted) setInsightMetrics((old) => ({ ...old, ...data.metrics }));
        } catch { if (controller.signal.aborted) break; }
      }
    };
    load();
    return () => controller.abort();
  }, [visibleOperators, selectedNetwork, delegatorComparisonHeight]);
  const availableMetrics = Object.values(insightMetrics);
  const columns = {
    delegators: availableMetrics.some((m) => hasNumber(m.delegators)),
    governance: availableMetrics.some((m) => hasNumber(m.governance?.total)),
    apr: availableMetrics.some((m) => hasNumber(m.apr)),
    oracle: availableMetrics.some((m) => hasNumber(m.oracle?.percent)),
    blocks: validators.some((v) => hasNumber(v.blocks?.percent)),
    website: validators.some((v) => hasValue(v.website)),
  };
  const totalPages = Math.ceil(filtered.length / pageSize);
  const statusCounts = validators.reduce((counts, validator) => {
    counts[healthStatus(validator)] += 1;
    if (!validator.jailed && ['Active', 'BOND_STATUS_BONDED'].includes(validator.status)) counts.active += 1;
    if (!validator.jailed && !['Active', 'BOND_STATUS_BONDED'].includes(validator.status)) counts.inactive += 1;
    return counts;
  }, { live: 0, trouble: 0, jailed: 0, inactive: 0, active: 0 });
  const selectedListData = validators.find((validator) => getOperator(validator) === selectedValidator) || {};
  const selectedHealth = selectedValidator ? healthStatus(selectedListData) : null;
  const selectedHealthLabel = selectedHealth === 'live' ? 'Live' : selectedHealth === 'jailed' ? 'Jailed' : !['Active', 'BOND_STATUS_BONDED'].includes(selectedListData.status) ? 'Inactive' : 'In trouble';

  useEffect(() => {
    if (!isCosmosNetwork) return undefined;
    const identities = [...new Set(currentValidators.map((validator) => String(validator.identity || '').trim().toUpperCase()))]
      .filter((identity) => /^[0-9A-F]{16,40}$/.test(identity) && !(identity in cosmosLogos));
    if (!identities.length) return undefined;
    const controller = new AbortController();
    axios.get(`${LCD}/api/cosmos/validator-logos`, {
      params: { identities: identities.slice(0, 40).join(',') }, signal: controller.signal,
    }).then((response) => {
      setCosmosLogos((previous) => ({ ...previous, ...(response.data?.logos || {}) }));
    }).catch(() => {});
    return () => controller.abort();
  }, [isCosmosNetwork, selectedNetwork, currentPage, validators, search, cosmosLogos]);

  const websiteIcon = (website) => {
    if (!website) return null;
    try {
      const host = new URL(/^https?:\/\//i.test(website) ? website : `https://${website}`).hostname;
      return host ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=128` : null;
    } catch { return null; }
  };
  const validatorLogo = (validator, operator) => {
    const primary = validator.logo || cosmosLogos[String(validator.identity || '').toUpperCase()];
    const fallback = isCosmosNetwork ? websiteIcon(validator.website) : null;
    if (logoErrors[operator] === 'both') return null;
    if (logoErrors[operator] === 'primary') return fallback;
    if (logoErrors[operator] === 'fallback') return primary;
    return primary || fallback;
  };

  // ---------------- Render ----------------
 
  return (
  <div className="validator-dashboard">
    <div className="validators-header">
      <h1 className="title">Validator Overview</h1>
      <p>{validatorsLoading ? 'Loading validators...' : isCosmosNetwork
        ? `Showing ${filtered.length} of ${validators.length} validators on ${selectedChain?.prettyName || 'this chain'}`
        : `Showing ${filtered.length} of ${validators.length} Terra Classic validators`}</p>

      <div className="validator-filters">
        <label htmlFor="validator-network">Cosmos chain
          <select id="validator-network" value={selectedNetwork} onChange={(e) => setNetworkParams({ chain: e.target.value === 'lunc' ? 'terra' : e.target.value })}>
            <option value="lunc">Terra Classic (LUNC)</option>
            {cosmosChains.map((chain) => <option key={chain.key} value={chain.key}>{chain.prettyName} ({chain.symbol})</option>)}
          </select>
        </label>
        <label htmlFor="validator-jump">Validator
          <select id="validator-jump" value={selectedValidator || ''} disabled={!validators.length} onChange={(e) => setSelectedValidator(e.target.value || null)}>
            <option value="">Choose a validator</option>
            {validators.map((validator) => <option key={getOperator(validator)} value={getOperator(validator)}>{validator.moniker} ({getOperator(validator).slice(0, 12)}…)</option>)}
          </select>
        </label>
        <label htmlFor="validator-status">Status
          <select id="validator-status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All statuses ({validators.length})</option>
            <option value="active">Active set ({statusCounts.active})</option>
            <option value="live">Live ({statusCounts.live})</option>
            <option value="inactive">Inactive ({statusCounts.inactive})</option>
            <option value="trouble">In trouble ({statusCounts.trouble})</option>
            <option value="jailed">Jailed ({statusCounts.jailed})</option>
          </select>
        </label>
        <label htmlFor="validator-search">Search validators
          <input id="validator-search" type="search" placeholder="Name or operator address" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      <p className="validator-status-note">In trouble: unbonding, unbonded, or below 95% block signing in the chain’s current window.</p>
      <div className="validator-change-controls">
        <span>Stake change</span>
        <div role="group" aria-label="Stake change period">
          {stakePeriods.map((period) => <button key={period.key} type="button" className={stakePeriod === period.key ? 'active' : ''} aria-pressed={stakePeriod === period.key} onClick={() => setStakePeriod(period.key)}>{period.label}</button>)}
        </div>
      </div>
      {validatorsError && <p className="validator-load-error" role="alert">{validatorsError}</p>}
    </div>

    <div className="validator-table-scroll">
      <table className="validator-table">
        <thead><tr><th>Validator</th><th>Total staked</th><th>Voting power</th>{columns.delegators && <th>Delegators</th>}{columns.governance && <th title="Participation in current voting proposals">Votes</th>}<th>Fee</th>{columns.apr && <th title="Annual inflation rewards after commission; excludes fees, extra rewards and compounding">Est. APR</th>}{columns.blocks && <th>Blocks</th>}{columns.oracle && <th>Oracle</th>}<th>Status</th>{columns.website && <th>Website</th>}</tr></thead>
        <tbody>
          {currentValidators.map((v) => {
            const op = getOperator(v);
            if (!op) return null;
            const logo = validatorLogo(v, op);
            const insights = insightMetrics[op];
            const status = healthStatus(v);
            const statusLabel = status === 'live' ? 'Live' : status === 'jailed' ? 'Jailed' : !['Active', 'BOND_STATUS_BONDED'].includes(v.status) ? 'Inactive' : 'In trouble';
            const stakeChange = v.stake_changes?.[stakePeriod];
            const rankDelta = stakeChange?.rankDelta;
            const hasRankChange = rankDelta !== null && rankDelta !== undefined && Number.isFinite(Number(rankDelta)) && Number(rankDelta) !== 0;
            const changeTitle = stakeChange?.comparedAt ? `Compared with on-chain stake at ${new Date(stakeChange.comparedAt).toLocaleString('en-GB')} (block ${Number(stakeChange.comparisonHeight).toLocaleString()})` : 'Historical stake is unavailable for this period';
            return <tr key={op} className={favoriteValidators.includes(op) ? 'favorite' : ''} onClick={() => setSelectedValidator(op)} onMouseEnter={() => prefetchDetails(op)}>
              <td data-label="Validator"><div className="validator-table-name">
                <span className="validator-rank-stack"><span className="validator-rank">{v.displayIndex}</span>{hasRankChange && <span className={`validator-rank-change ${changeDirection(rankDelta)}`} title={`Rank change: ${changeTitle}`}>{formatRankChange(rankDelta)}</span>}</span>
                {logo ? <img src={logo} alt="" className="validator-table-logo" loading="lazy" onError={() => setLogoErrors((previous) => ({ ...previous, [op]: logo === websiteIcon(v.website) ? (previous[op] === 'primary' ? 'both' : 'fallback') : (previous[op] === 'fallback' ? 'both' : 'primary') }))} /> : <span className="validator-table-initials" aria-hidden="true">{String(v.moniker || '?').trim().slice(0, 2).toUpperCase()}</span>}
                <button type="button" className="validator-table-open" onClick={(event) => { event.stopPropagation(); setSelectedValidator(op); }}>{v.moniker}</button>
                <button type="button" className={`validator-table-favorite ${favoriteValidators.includes(op) ? 'active' : ''}`} aria-label={`${favoriteValidators.includes(op) ? 'Remove' : 'Add'} ${v.moniker} ${favoriteValidators.includes(op) ? 'from' : 'to'} favorites`} title="Toggle favorite" onClick={(event) => { event.stopPropagation(); toggleFavorite(op); }}>{favoriteValidators.includes(op) ? '★' : '☆'}</button>
              </div></td>
              <td data-label="Total staked"><div>{hasNumber(v.tokens) && <>{formatStake(v.tokens)} <span className="validator-table-unit">{selectedSymbol}</span></>}</div>{hasNumber(stakeChange?.microDelta) && <small className={`validator-stake-change ${changeDirection(stakeChange.microDelta)}`} title={changeTitle}>{formatStakeChange(stakeChange.microDelta, denomExponent, selectedSymbol)}</small>}</td>
              <td data-label="Voting power"><span className="validator-voting-power" style={{ '--power': `${Math.min(Number(v.voting_power) || 0, 100)}%` }}>{formatPercent(v.voting_power)}</span>{hasNumber(stakeChange?.powerDelta) && <small className={`validator-stake-change ${changeDirection(stakeChange.powerDelta)}`} title={changeTitle}>{formatPowerChange(stakeChange.powerDelta)}</small>}</td>
              {columns.delegators && <td data-label="Delegators">{hasNumber(insights?.delegators) ? formatCount(insights.delegators) : null}{hasNumber(insights?.delegatorDelta) && Number(insights.delegatorDelta) !== 0 && Number(insights.delegatorComparisonHeight) === Number(delegatorComparisonHeight) && <small className={`validator-stake-change ${changeDirection(insights.delegatorDelta)}`} title={`Compared with block ${Number(insights.delegatorComparisonHeight).toLocaleString()}`}>{Number(insights.delegatorDelta) > 0 ? '+' : '−'}{formatCount(Math.abs(insights.delegatorDelta))}</small>}</td>}
              {columns.governance && <td data-label="Votes" title="Current voting proposals">{hasNumber(insights?.governance?.total) ? `${insights.governance.voted} / ${insights.governance.total}` : null}</td>}
              <td data-label="Fee">{hasNumber(v.commission) ? formatCommission(v.commission) : null}{hasNumber(insights?.commissionRates?.dynamicMinimum) && <small className="validator-dynamic-fee" title={`Dynamic minimum ${formatPercent(insights.commissionRates.dynamicMinimum * 100)}`}>Dynamic</small>}</td>
              {columns.apr && <td data-label="Est. APR" title={insights?.aprMethod}>{hasNumber(insights?.apr) ? formatPercent(insights.apr) : null}</td>}
              {columns.blocks && <td data-label="Blocks">{hasNumber(v.blocks?.percent) && <span className="validator-table-pill" title={`${formatCount(v.blocks.signed)} signed of ${formatCount(v.blocks.total)}`}>{formatPercent(v.blocks.percent)}</span>}</td>}
              {columns.oracle && <td data-label="Oracle">{hasNumber(insights?.oracle?.percent) && <span className="validator-table-pill" title={`${formatCount(insights.oracle.proposed)} proposed of ${formatCount(insights.oracle.total)}`}>{formatPercent(insights.oracle.percent)}</span>}</td>}
              <td data-label="Status"><span className={`validator-table-status ${status}`} title={`${statusLabel} · ${v.status || 'Unknown on-chain status'}${v.jailed ? ' · jailed on-chain' : ''}`}>{statusLabel}</span></td>
              {columns.website && <td data-label="Website">{hasValue(v.website) ? renderWebsiteLink(v.website, true) : null}</td>}
            </tr>;
          })}
        </tbody>
      </table>
    </div>

    {totalPages > 1 && (
      <div className="pagination">
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={i}
            type="button"
            className={currentPage === i + 1 ? 'active' : ''}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </button>
        ))}
      </div>
    )}

    {selectedValidator && (
      <>
        <div
          className="validator-overlay"
          onClick={() => setSelectedValidator(null)}
        />

        <div className="validator-modal">
          <button
            type="button"
            className="close-button"
            onClick={() => setSelectedValidator(null)}
          >
            ✕
          </button>

          {validatorDetails ? <ValidatorProfile
            key={selectedValidator}
            details={validatorDetails} listData={selectedListData} insights={profileInsights || insightMetrics[selectedValidator]}
            chainKey={selectedNetwork === 'lunc' ? 'terra' : selectedNetwork}
            chainName={isCosmosNetwork ? selectedChain?.prettyName : 'Terra Classic'}
            symbol={selectedSymbol} exponent={denomExponent} cosmos={isCosmosNetwork}
            logo={validatorLogo(validatorDetails, selectedValidator)} status={selectedHealthLabel} error={detailsError}
            activity={!selectedListData.jailed && ['Active', 'BOND_STATUS_BONDED'].includes(selectedListData.status)
              ? <ValidatorActivityPanels key={selectedValidator} lcd={LCD} operator={selectedValidator} chainKey={isCosmosNetwork ? selectedNetwork : undefined} /> : null}
          /> : <p role="status">Loading validator details…</p>}
        </div>
      </>
    )}
  </div>
);

};

export default CryptoPrices;
